import axios from 'axios'
import api from './client'
import { extractMembers, extractTotalItems } from './hydra'
import type { FloorPlanDocument } from '@/utils/floorPlan'

export type ApiTokenScope = 'document_upload' | 'firmware_upload' | 'device_communication' | 'backup_reporting' | 'server_stats_reporting' | 'mail_ingest'

function resolveApiDocumentUrl(url: string): string {
  if (typeof window === 'undefined' || '' === url.trim()) {
    return url
  }

  if (/^https?:\/\//i.test(url)) {
    return url
  }

  const configuredBaseUrl = typeof api.defaults.baseURL === 'string' ? api.defaults.baseURL : '/api'
  const baseUrl = /^https?:\/\//i.test(configuredBaseUrl)
    ? configuredBaseUrl
    : new URL(configuredBaseUrl, window.location.origin).toString()

  return new URL(url, baseUrl).toString()
}

export interface ApiTokenRecord {
  id: number
  name: string
  scopes: ApiTokenScope[]
  createdAt: string
  lastUsedAt: string | null
  plainToken?: string | null
  user?: Record<string, unknown> | null
  device?: Record<string, unknown> | null
}

function normalizeDeviceRebootState<T extends Record<string, unknown>>(device: T): T & { reboot_requested: boolean } {
  return {
    ...device,
    reboot_requested: Boolean(device.reboot_requested ?? device.reboot ?? false),
  }
}

export interface SessionRecord {
  [key: string]: unknown
  id: number
  user: {
    id: number | null
    email: string | null
    displayName: string | null
  } | null
  userAgent: string | null
  ipAddress: string | null
  countryCode: string | null
  countryName: string | null
  createdAt: string
  lastUsedAt: string
  expiresAt: string
}

export interface SecurityLoginAttemptRecord {
  [key: string]: unknown
  id: number | null
  success: boolean
  failureReason: string | null
  email: string | null
  ipAddress: string | null
  countryCode: string | null
  countryName: string | null
  userAgent: string | null
  createdAt: string
  user: {
    id: number | null
    email: string | null
    displayName: string | null
  } | null
}

export interface SecurityStatsRecord {
  summary: {
    successfulLogins24h: number
    successfulLogins7d: number
    failedLogins24h: number
    failedLogins7d: number
    uniqueIps24h: number
    activeSessions: number
  }
  topCountries: Array<{
    countryCode: string | null
    countryName: string
    total: number
    successful: number
    failed: number
  }>
  topIps: Array<{
    ipAddress: string
    countryCode: string | null
    countryName: string
    total: number
    successful: number
    failed: number
  }>
  timeline: Array<{
    bucket: string
    successful: number
    failed: number
  }>
  recentAttempts: SecurityLoginAttemptRecord[]
}

export interface AdminDiagnosticCheckRecord {
  key: string
  label: string
  status: 'ok' | 'warning' | 'error'
  message: string
  details: string | null
  resolutionHint: string | null
}

export interface AdminDiagnosticsRecord {
  status: 'ok' | 'warning' | 'error'
  summary: {
    total: number
    ok: number
    warning: number
    error: number
  }
  checks: AdminDiagnosticCheckRecord[]
  checkedAt: string
  stale: boolean
  cacheAgeSeconds: number
  cacheMaxAgeSeconds: number
}

export interface ServerBackupTypeRecord {
  id: number
  key: string
  name: string
  expected_interval_hours: number | null
  observed_interval_hours: number | null
  last_reported_at: string | null
  last_successful_at: string | null
  last_success: boolean | null
  last_size_bytes: number | null
  last_artifact_name: string | null
  last_error_message: string | null
  is_overdue: boolean
}

export interface ServerBackupApplicationRecord {
  id: number
  key: string
  name: string
  current_storage_bytes: number | null
  current_storage_updated_at: string | null
  last_reported_at: string | null
  backup_types: ServerBackupTypeRecord[]
}

export interface ServerBackupOverviewRecord {
  generated_at: string
  summary: {
    application_count: number
    backup_type_count: number
    failed_backup_type_count: number
    overdue_backup_type_count: number
  }
  applications: ServerBackupApplicationRecord[]
}

export interface ServerBackupHistoryItemRecord {
  id: number
  application: {
    id: number | null
    key: string
    name: string
  }
  backup_type: {
    id: number | null
    key: string
    name: string
  }
  artifact_name: string | null
  backup_created_at: string
  reported_at: string
  success: boolean
  size_bytes: number | null
  application_storage_bytes: number | null
  expected_interval_hours: number | null
  error_message: string | null
}

export interface ServerBackupHistoryRecord {
  items: ServerBackupHistoryItemRecord[]
}

export interface InventoryLabelDefinitionRecord extends Record<string, unknown> {
  id: number
  name: string
  pageWidthMm: number
  pageHeightMm: number
  labelWidthMm: number
  labelHeightMm: number
  columns: number
  rows: number
  marginTopMm: number
  marginLeftMm: number
  horizontalGapMm: number
  verticalGapMm: number
  active: boolean
  defaultDefinition: boolean
  presetKey: string | null
  totalSlots: number
}

export interface InventoryLabelSheetPlacementRecord {
  id: number
  slotIndex: number
  row: number
  column: number
  itemId: number
  itemName: string
  itemQrCode: string
  itemIdentifier: string
  showQrCode: boolean
  showName: boolean
  attributeLines: string[]
}

export interface InventoryLabelSheetRecord extends Record<string, unknown> {
  id: number
  definition: InventoryLabelDefinitionRecord
  status: 'open' | 'printed' | 'archived'
  createdAt: string
  printedAt: string | null
  occupiedSlotCount: number
  freeSlotCount: number
  placementSummaries: InventoryLabelSheetPlacementRecord[]
}

export interface InventoryLabelSheetCreateResult {
  id: number
  definitionId: number
  definitionName: string
  status: InventoryLabelSheetRecord['status']
}

export interface InventoryLabelSheetAssignResult {
  assignedItemCount: number
  sheetIds: number[]
  createdSheetIds: number[]
}

export interface ApiLocationSummary extends Record<string, unknown> {
  id: number
  city?: string | null
  name?: string | null
  '@id'?: string
}

export interface GroupSummaryRecord extends Record<string, unknown> {
  id: number
  name?: string | null
  location?: ApiLocationSummary | null
  widgets?: Array<Record<string, unknown> & { id: number }>
}

export interface WeatherForecastSupplierRecord extends Record<string, unknown> {
  id: number
  name?: string | null
  api_url?: string | null
  custom_placeholders?: string[] | null
}

export interface WidgetSettingRecord extends Record<string, unknown> {
  id: number
  key: string
  value: string
}

export interface SensorSettingRecord extends Record<string, unknown> {
  id: number
  key: string
  value: string
}

export interface SensorSummaryRecord extends Record<string, unknown> {
  id: number
  name?: string | null
  sensor_type?: string | null
  current?: number | null
  avg?: number | null
  min?: number | null
  max?: number | null
  short_avg?: number | null
  current_date?: string | null
  unit?: string | null
  description?: string | null
  keep_for_n_days?: number | null
  aggregation_enabled?: boolean | null
  /** Nur an Verbrauchszählern gesetzt; siehe `can_have_meter_number`. */
  meterNumber?: string | null
  /** true bei Wasser-, Gas- und Stromzählern — sonst lehnt die API eine Zählernummer ab. */
  can_have_meter_number?: boolean | null
  device?: Record<string, unknown> | null
  sensor_settings?: SensorSettingRecord[]
}

export interface SensorRecord extends SensorSummaryRecord {}

export interface SensorDeletePreview {
  sensor: {
    id: number
    name: string
    sensorType: string
  }
  device: {
    id: number
    name: string
  } | null
  widgets: Array<{
    id: number
    description: string
    widgetType: string
  }>
  billingProperties: Array<{
    id: number
    name: string
  }>
  billingRentalUnits: Array<{
    id: number
    name: string
    unitNumber: string
  }>
  counts: {
    sensorSettings: number
    sensorMeasurements: number
    rainMeasurements: number
    sensorDateCache: number
    sensorDailyCache: number
    sensorMonthlyCache: number
    accumulatedSensorValues: number
    widgetLinks: number
    billingPropertyLinks: number
    billingRentalUnitLinks: number
    meterReadingsDetached: number
  }
}

export interface WidgetRecord extends Record<string, unknown> {
  id: number
  description?: string | null
  widget_type?: string | null
  widget_order?: number
  widget_width?: number
  widget_height?: number
  widget_settings?: WidgetSettingRecord[]
  sensors?: SensorSummaryRecord[]
  weather_suppliers?: WeatherForecastSupplierRecord[]
}

/* ── Auth ──────────────────────────────────────────────────────────────── */
export async function login(email: string, password: string) {
  const { data } = await api.post('/auth/login', { email, password })
  return data.token as string
}

/* ── Sessions ──────────────────────────────────────────────────────────── */
export async function fetchSessions() {
  const { data } = await api.get('/admin/sessions')
  return (data.items ?? []) as SessionRecord[]
}

export async function deleteSession(id: number) {
  const { data } = await api.delete(`/admin/session/${id}`)
  return data as { success: boolean; currentSession: boolean }
}

export async function fetchSecurityStats() {
  const { data } = await api.get('/admin/security/stats')
  return data as SecurityStatsRecord
}

export async function fetchAdminDiagnostics() {
  const { data } = await api.get('/admin/diagnostics')
  return data as AdminDiagnosticsRecord
}

export async function refreshAdminDiagnostics() {
  const { data } = await api.post('/admin/diagnostics/refresh')
  return data as AdminDiagnosticsRecord
}

/* ── Locations ─────────────────────────────────────────────────────────── */

export async function fetchLocations() {
  const { data } = await api.get('/locations', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createWeatherForecastEntry(payload: {
  location_id: number
  supplier_id: number
  fetch_every_x_seconds?: number
}) {
  const { data } = await api.post('/admin/widget/weather-forecast', payload)
  return data as { success: boolean; created: boolean; id: number }
}

export async function fetchLocation(id: number) {
  const { data } = await api.get(`/locations/${id}`)
  return data
}

export async function createLocation(payload: Record<string, unknown>) {
  const { data } = await api.post('/locations', payload)
  return data
}

export async function updateLocation(id: number, payload: Record<string, unknown>) {
  const { data } = await api.put(`/locations/${id}`, payload)
  return data
}

export async function deleteLocation(id: number) {
  await api.delete(`/locations/${id}`)
}

export async function fetchLocationMovePreview(sourceId: number, targetId: number) {
  const { data } = await api.get(`/admin/location/${sourceId}/move-preview`, { params: { target: targetId } })
  return data as {
    source: { id: number; name: string }
    target: { id: number; name: string }
    devices: { id: number; name: string }[]
    groups: { id: number; name: string }[]
  }
}

export async function moveLocation(sourceId: number, targetId: number) {
  const { data } = await api.post(`/admin/location/${sourceId}/move`, { target_location_id: targetId })
  return data as { success: boolean; moved_devices: number; moved_groups: number }
}

/* ── Groups ────────────────────────────────────────────────────────────── */
export async function fetchGroups(locationId?: number) {
  const params: Record<string, unknown> = { pagination: 'false' }
  if (locationId) params['location.id'] = locationId
  const { data } = await api.get('/groups', { params })
  return extractMembers(data)
}

export async function fetchGroup(id: number) {
  const { data } = await api.get(`/groups/${id}`)
  return data
}

export async function createGroup(payload: Record<string, unknown>) {
  const { data } = await api.post('/groups', payload)
  return data
}

export async function updateGroup(id: number, payload: Record<string, unknown>) {
  const { data } = await api.put(`/groups/${id}`, payload)
  return data
}

export async function deleteGroup(id: number) {
  await api.delete(`/groups/${id}`)
}

/* ── Devices ───────────────────────────────────────────────────────────── */
export async function fetchDevices() {
  const { data } = await api.get('/devices', { params: { pagination: 'false' } })
  const members = extractMembers(data)

  return members.map((device: Record<string, unknown>) => normalizeDeviceRebootState(device))
}

export async function fetchDeviceUptimeOverview(date: string) {
  const { data } = await api.get('/admin/device/uptime-overview', {
    params: { date },
  })

  return data as {
    date: string
    items: Array<{
      device_id: number
      current_status: 'online' | 'offline' | 'no_data'
      uptime_percent: number
      segments: Array<'online' | 'offline' | 'no_data'>
    }>
  }
}

export async function fetchDevice(id: number) {
  const { data } = await api.get(`/devices/${id}`)
  return normalizeDeviceRebootState(data as Record<string, unknown>)
}

export interface DeviceDeletePreview {
  device: {
    id: number
    name: string
  }
  sensors: Array<{
    id: number
    name: string
    sensorType: string
  }>
  counts: {
    sensors: number
    sensorSettings: number
    sensorMeasurements: number
    rainMeasurements: number
    sensorDateCache: number
    sensorDailyCache: number
    sensorMonthlyCache: number
    accumulatedSensorValues: number
    widgetSensorLinks: number
    billingPropertySensorLinks: number
    billingRentalUnitSensorLinks: number
    deviceSettings: number
    deviceLogs: number
    coreDumps: number
    apiTokens: number
    firmwareAssignments: number
    uptimeHeartbeats: number
    deviceActionTriggers: number
  }
}

export async function fetchDeviceDeletePreview(id: number) {
  const { data } = await api.get(`/admin/device/${id}/delete-preview`)
  return data as DeviceDeletePreview
}

export async function createDevice(payload: Record<string, unknown>) {
  const { data } = await api.post('/devices', payload)
  return data
}

export async function updateDevice(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/devices/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteDevice(id: number) {
  await api.delete(`/admin/device/${id}`)
}

export async function regenerateDeviceTokens(id: number) {
  const { data } = await api.post(`/admin/device/${id}/regenerate-tokens`)
  return data as { device_token: string }
}

export async function requestDeviceReboot(id: number) {
  const { data } = await api.post(`/admin/device/${id}/reboot`)
  return data as { success: boolean }
}

export async function fetchDeviceSettingsProfiles(deviceId: number) {
  const { data } = await api.get(`/admin/device/${deviceId}/settings`)
  return data.items ?? []
}

export async function createDeviceSettingsProfile(
  deviceId: number,
  payload: { protocol_version: number; settings_json: string; editor_type?: string },
) {
  const { data } = await api.post(`/admin/device/${deviceId}/settings`, payload)
  return data
}

export async function updateDeviceSettingsProfile(
  deviceId: number,
  settingId: number,
  payload: { protocol_version: number; settings_json: string; editor_type?: string },
) {
  const { data } = await api.patch(`/admin/device/${deviceId}/settings/${settingId}`, payload)
  return data
}

export async function deleteDeviceSettingsProfile(deviceId: number, settingId: number) {
  await api.delete(`/admin/device/${deviceId}/settings/${settingId}`)
}

export async function fetchDeviceLogs(options: {
  device: string
  level?: string
  from?: string
  to?: string
  page?: number
}) {
  const params: Record<string, string | number> = {
    device: options.device,
    'order[date]': 'desc',
    page: options.page ?? 1,
  }

  if (options.level) params.level = options.level
  if (options.from) params['date[after]'] = new Date(options.from).toISOString()
  if (options.to) params['date[before]'] = new Date(options.to).toISOString()

  const { data } = await api.get('/device_log_messages', { params })

  return {
    items: extractMembers(data),
    totalItems: extractTotalItems(data),
  }
}

export async function fetchDeviceCoreDumps(device: string) {
  const { data } = await api.get('/core_dumps', {
    params: {
      device,
      'order[date]': 'desc',
      pagination: 'false',
    },
  })

  return extractMembers(data)
}

export async function fetchDeviceUptime(deviceId: number, date: string) {
  const { data } = await api.get(`/admin/device/${deviceId}/uptime`, {
    params: { date },
  })

  return data as {
    date: string
    bin_size_minutes: number
    bins: Array<{
      start: string
      end: string
      status: 'online' | 'offline' | 'no_data'
      online_minutes: number
      offline_minutes: number
      no_data_minutes: number
    }>
    stats: {
      online_minutes: number
      offline_minutes: number
      no_data_minutes: number
      uptime_percent: number
      transition_count: number
      total_bins: number
      online_bins: number
      offline_bins: number
      no_data_bins: number
    }
  }
}

export async function deleteDeviceCoreDump(id: number) {
  await api.delete(`/core_dumps/${id}`)
}

export async function downloadDeviceCoreDump(deviceId: number, dumpId: number) {
  const response = await api.get(`/admin/device/${deviceId}/core-dumps/${dumpId}/download`, {
    responseType: 'blob',
  })

  const disposition = String(response.headers['content-disposition'] ?? '')
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  const encodedName = utf8Match?.[1] ?? plainMatch?.[1] ?? `core-dump-${dumpId}.bin`

  return {
    blob: response.data as Blob,
    filename: decodeURIComponent(encodedName),
  }
}

/* ── Device Action Triggers ────────────────────────────────────────────── */
export async function triggerDeviceAction(payload: { device: string; action_key: string; value?: string }) {
  const { data } = await api.post('/device_action_triggers', payload)
  return data
}

export async function fetchDeviceActionTrigger(id: number) {
  const { data } = await api.get(`/device_action_triggers/${id}`)
  return data
}

/* ── Sensors ───────────────────────────────────────────────────────────── */
export async function fetchSensors(deviceId?: number) {
  const params: Record<string, unknown> = { pagination: 'false' }
  if (deviceId) params.device = `/api/devices/${deviceId}`
  const { data } = await api.get('/sensors', { params })
  return extractMembers(data)
}

export async function fetchSensorsByType(sensorType: string) {
  const { data } = await api.get('/sensors', { params: { pagination: 'false' } })
  const members = extractMembers(data)
  return members.filter((s: Record<string, unknown>) => s.sensor_type === sensorType)
}

export async function fetchSensor(id: number) {
  const { data } = await api.get(`/sensors/${id}`)
  return data as SensorRecord
}

export async function updateSensor(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/sensors/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function createSensor(payload: { device_id: number; sensor_type: string; name: string; description?: string; keep_for_n_days?: number; meter_number?: string | null }) {
  const { data } = await api.post('/admin/sensor', payload)
  return data
}

export async function fetchSensorDeletePreview(id: number) {
  const { data } = await api.get(`/admin/sensor/${id}/delete-preview`)
  return data as SensorDeletePreview
}

export async function deleteSensor(id: number) {
  await api.delete(`/admin/sensor/${id}`)
}

export async function fetchSensorStats(id: number, hours = 24) {
  const { data } = await api.get(`/sensor/${id}/stats`, { params: { hours } })
  return data
}

/* ── Sensor Measurements ───────────────────────────────────────────────── */
export async function fetchSensorMeasurements(sensorId: number, page = 1) {
  const { data } = await api.get('/sensor_measurements', {
    params: { 'sensor': sensorId, 'order[date]': 'desc', page },
  })
  return {
    items: extractMembers(data),
    totalItems: extractTotalItems(data),
  }
}

export async function updateSensorMeasurement(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/sensor_measurements/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteSensorMeasurement(id: number) {
  await api.delete(`/sensor_measurements/${id}`)
}

export async function fetchSensorAvailableMonths(id: number) {
  const { data } = await api.get(`/sensor/${id}/available-months`)
  return data.months
}

export async function fetchSensorAvailableDays(id: number, year: number, month: number) {
  const { data } = await api.get(`/sensor/${id}/available-days`, { params: { year, month } })
  return data.days
}

export async function fetchSensorHourly(id: number, date: string) {
  const { data } = await api.get(`/sensor/${id}/hourly`, { params: { date } })
  return data.hourly
}

export async function fetchSensorRange(id: number, from: string, to: string) {
  const { data } = await api.get(`/sensor/${id}/range`, { params: { from, to } })
  return data.data
}

export async function createSensorMeasurement(sensorId: number, value: number, date?: string) {
  const payload: Record<string, unknown> = { value }
  if (date) payload.date = date
  const { data } = await api.post(`/sensors/${sensorId}/measurements`, payload)
  return data
}

export async function createSensorSetting(sensorId: number, payload: { key: string; value: string }) {
  const { data } = await api.post(`/admin/sensor/${sensorId}/setting`, payload)
  return data as { success: boolean; id: number; key: string }
}

export async function updateSensorSetting(sensorId: number, settingId: number, payload: { key?: string; value?: string }) {
  const { data } = await api.put(`/admin/sensor/${sensorId}/setting/${settingId}`, payload)
  return data as { success: boolean; id: number; merged?: boolean }
}

export async function deleteSensorSetting(sensorId: number, settingId: number) {
  await api.delete(`/admin/sensor/${sensorId}/setting/${settingId}`)
}

export async function fetchWaterStats(sensorId: number, year?: number) {
  const params: Record<string, unknown> = {}
  if (year) params.year = year
  const { data } = await api.get(`/sensor/${sensorId}/water-stats`, { params })
  return data
}

export async function fetchWaterDaily(sensorId: number, year: number, month: number) {
  const { data } = await api.get(`/sensor/${sensorId}/water-stats/daily`, { params: { year, month } })
  return data.daily as Array<{ day: number; consumption: number }>
}

export async function fetchWaterYearly(sensorId: number, years = 5) {
  const { data } = await api.get(`/sensor/${sensorId}/water-stats/yearly`, { params: { years } })
  return data.yearly as Array<{ year: number; consumption: number }>
}

export interface RainYearlyStats {
  sensor_id: number
  currentYear: number
  currentYearTotal: number
  previousYearTotal: number | null
  yearOverYearPercent: number | null
  rainDaysThisYear: number
  recordYear: { year: number; total: number } | null
  recordDay: { date: string; total: number } | null
  avgAnnualTotal: number | null
  yearsAvailable: number
}

export async function fetchRainStats(sensorId: number) {
  const { data } = await api.get(`/sensor/${sensorId}/rain-stats`)
  return data as RainYearlyStats
}

/* ── Widgets ───────────────────────────────────────────────────────────── */
export async function fetchWidgets(groupId?: number) {
  const params: Record<string, unknown> = { pagination: 'false' }
  if (groupId) params['groups.id'] = groupId
  const { data } = await api.get('/widgets', { params })
  return extractMembers(data)
}

export async function fetchWidget(id: number) {
  const { data } = await api.get(`/widgets/${id}`)
  return data as WidgetRecord
}

export async function updateWidget(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/admin/widget/${id}`, payload)
  return data
}

export async function createWidget(payload: Record<string, unknown>) {
  const { data } = await api.post('/admin/widget', payload)
  return data
}

export async function deleteWidget(id: number) {
  await api.delete(`/widgets/${id}`)
}

/* ── Group Widget Management ────────────────────────────────────────── */
export async function addWidgetToGroup(groupId: number, widgetId: number) {
  const { data } = await api.post(`/admin/group/${groupId}/widget/${widgetId}`)
  return data
}

export async function removeWidgetFromGroup(groupId: number, widgetId: number) {
  await api.delete(`/admin/group/${groupId}/widget/${widgetId}`)
}

/* ── Widget Sensor Management ───────────────────────────────────────── */
export async function addSensorToWidget(widgetId: number, sensorId: number) {
  const { data } = await api.post(`/admin/widget/${widgetId}/sensor/${sensorId}`)
  return data
}

export async function removeSensorFromWidget(widgetId: number, sensorId: number) {
  await api.delete(`/admin/widget/${widgetId}/sensor/${sensorId}`)
}

/* ── Widget Device Config ──────────────────────────────────────────────── */
export interface WidgetDevicePlantPotSchedule {
  time: string
  seconds: number
  liters: number
}

export interface WidgetDevicePlantPot {
  name: string | null
  moisture_sensor_id: number | null
  schedule: WidgetDevicePlantPotSchedule[]
}

export interface WidgetDeviceWaterLevelSensor {
  sensor_id: number | null
}

export interface WidgetDeviceAction {
  id: string
  label: string
  value_type: string | null
  input_label: string | null
  default_value: string | null
}

export interface WidgetDeviceConfig {
  plant_pots: WidgetDevicePlantPot[]
  water_level_sensor: WidgetDeviceWaterLevelSensor | null
  actions: WidgetDeviceAction[]
  device_id: number | null
}

export async function fetchWidgetDeviceConfig(widgetId: number) {
  const { data } = await api.get(`/widget/${widgetId}/device-config`)
  return data as WidgetDeviceConfig
}

/** Vom Gerät gemeldeter Gießvorgang; `pot` ist der Index in `plant_pots`. */
export interface WateringEvent {
  pot: number
  started_at: string
  seconds: number
  liters: number | null
  source: string | null
}

export async function fetchWidgetWateringHistory(widgetId: number, days = 7) {
  const { data } = await api.get(`/widget/${widgetId}/watering-history`, { params: { days } })
  return (data.events ?? []) as WateringEvent[]
}

/* ── Widget Grid ───────────────────────────────────────────────────────── */
export async function saveWidgetGrid(
  gridOptions: Record<number, { order: number; width: number; height: number }>,
) {
  const { data } = await api.post('/widget/grid', { grid_options: gridOptions })
  return data
}

/* ── CronJobs ──────────────────────────────────────────────────────────── */
export async function fetchCronJobs() {
  const { data } = await api.get('/cron_jobs', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function runCronJobs(force = false) {
  const { data } = await api.post(`/cron/run${force ? '?force=1' : ''}`)
  return data
}

export async function updateCronJob(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/cron_jobs/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteCronJob(id: number) {
  await api.delete(`/admin/cron/${id}`)
}

export async function runSingleCronJob(id: number) {
  const { data } = await api.post(`/admin/cron/${id}/run`)
  return data
}

export async function fetchCronJob(id: number) {
  const { data } = await api.get(`/cron_jobs/${id}`)
  return data
}

export async function fetchCronJobLogs(cronJobId?: number) {
  const params = cronJobId ? `?cron_job=${cronJobId}&order[executed_at]=desc&pagination=false` : '?order[executed_at]=desc&pagination=false'
  const { data } = await api.get(`/cron_job_logs${params}`)
  return extractMembers(data)
}

/* ── Dashboard ─────────────────────────────────────────────────────────── */
export async function fetchDashboard(options?: { summary?: boolean; timeoutMs?: number }) {
  const { data } = await api.get('/dashboard', {
    params: options?.summary ? { summary: '1' } : undefined,
    timeout: options?.timeoutMs,
  })
  return data
}

/* ── Health ─────────────────────────────────────────────────────────────── */
export async function fetchHealth() {
  const { data } = await api.get('/health')
  return data
}

/* ── Users ──────────────────────────────────────────────────────────────── */
export async function fetchUsers() {
  const { data } = await api.get('/users', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchUser(id: number) {
  const { data } = await api.get(`/users/${id}`)
  return data
}

export async function createUser(payload: Record<string, unknown>) {
  const { data } = await api.post('/users', payload)
  return data
}

export async function updateUser(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/users/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteUser(id: number) {
  await api.delete(`/users/${id}`)
}

export async function resetUserPassword(id: number) {
  const { data } = await api.post(`/admin/users/${id}/reset-password`)
  return data
}

export async function fetchMe() {
  const { data } = await api.get('/auth/me')
  return data as { id: number; email: string; displayName: string; roles: string[]; apps: string[] }
}

export async function fetchAppModules() {
  const { data } = await api.get('/admin/app_modules')
  return data as Array<{ value: string; label: string }>
}

/* ── E-Mail-Upload (persönliche Upload-Adresse) ───────────────────────────── */
export interface MailUploadSettings {
  available: boolean
  enabled: boolean
  address: string | null
  senderAddress: string
}

export async function fetchMailUploadSettings() {
  const { data } = await api.get('/mail-upload')
  return data as MailUploadSettings
}

export async function enableMailUpload() {
  const { data } = await api.post('/mail-upload/enable')
  return data as MailUploadSettings
}

export async function regenerateMailUploadAddress() {
  const { data } = await api.post('/mail-upload/regenerate')
  return data as MailUploadSettings
}

export async function disableMailUpload() {
  const { data } = await api.post('/mail-upload/disable')
  return data as MailUploadSettings
}

/* ── Push-Benachrichtigungen ──────────────────────────────────────────────── */
export interface NotificationTypeInfo {
  key: string
  label: string
  description: string
}

/**
 * Nur die Arten, die dieser Nutzer tatsächlich bekommen kann. Dieselbe Regel
 * entscheidet serverseitig über den Versand – die Liste kann also nichts
 * versprechen, was nie ankommt.
 */
export async function fetchPushNotificationTypes() {
  const { data } = await api.get('/push/notification-types')
  return (data as { types: NotificationTypeInfo[] }).types
}

export async function fetchPushPublicKey() {
  const { data } = await api.get('/push/public-key')
  return (data as { publicKey: string }).publicKey
}

export async function savePushSubscription(subscription: PushSubscriptionJSON) {
  const { data } = await api.post('/push/subscriptions', subscription)
  return data as { error: boolean; message: string }
}

export async function deletePushSubscription(endpoint: string) {
  // DELETE mit Body: der Endpoint ist die Kennung des Geräts und gehört nicht
  // in eine URL (Länge, Logs).
  await api.delete('/push/subscriptions', { data: { endpoint } })
}

export async function sendTestPush() {
  const { data } = await api.post('/push/test')
  return data as { error: boolean; delivered: number; message: string }
}

/* ── Password Setup (Einladungs-Flow) ─────────────────────────────────────── */
export async function validatePasswordSetupToken(token: string) {
  const { data } = await api.get(`/password-setup/${encodeURIComponent(token)}`)
  return data as { valid: boolean; email: string }
}

export async function confirmPasswordSetup(token: string, password: string) {
  const { data } = await api.post('/password-setup/confirm', { token, password })
  return data
}

/* ── System Info ───────────────────────────────────────────────────────── */
export async function fetchSystemInfo(options?: { timeoutMs?: number }) {
  const { data } = await api.get('/system/info', {
    timeout: options?.timeoutMs,
  })
  return data
}

export async function fetchBackupOverview() {
  const { data } = await api.get('/server/backups/overview')
  return data as ServerBackupOverviewRecord
}

export async function fetchBackupHistory(options?: { applicationId?: number | null; backupTypeId?: number | null; limit?: number }) {
  const { data } = await api.get('/server/backups/history', {
    params: {
      application_id: options?.applicationId ?? undefined,
      backup_type_id: options?.backupTypeId ?? undefined,
      limit: options?.limit ?? 12,
    },
  })

  return data as ServerBackupHistoryRecord
}

export async function deleteBackupType(backupTypeId: number) {
  await api.delete(`/server/backups/types/${backupTypeId}`)
}

/* ── Weather ───────────────────────────────────────────────────────────── */
export async function fetchWeatherForecasts() {
  const { data } = await api.get('/weather_forecasts', { params: { pagination: 'false' } })
  return extractMembers(data)
}

/* ── Firmware ──────────────────────────────────────────────────────────── */
export async function fetchFirmwareVersions() {
  const { data } = await api.get('/device_firmware_versions', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchFirmwareVersion(id: number) {
  const { data } = await api.get(`/device_firmware_versions/${id}`)
  return data
}

export async function fetchDeviceFirmwares() {
  const { data } = await api.get('/device_firmware', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function downloadFirmwareFile(firmwareId: number, type: 'bin' | 'elf') {
  const response = await api.get(`/admin/firmware/${firmwareId}/download/${type}`, {
    responseType: 'blob',
  })

  const disposition = String(response.headers['content-disposition'] ?? '')
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  const encodedName = utf8Match?.[1] ?? plainMatch?.[1] ?? `firmware-${firmwareId}.${type}`

  return {
    blob: response.data as Blob,
    filename: decodeURIComponent(encodedName),
  }
}

/* ── API Tokens ─────────────────────────────────────────────────────────── */
export async function fetchApiTokens() {
  const { data } = await api.get('/api_tokens', { params: { pagination: 'false' } })
  return extractMembers<ApiTokenRecord>(data)
}

export async function createApiToken(payload: { name: string; scopes: ApiTokenScope[] }) {
  const { data } = await api.post('/api_tokens', payload)
  return data as ApiTokenRecord
}

export async function deleteApiToken(id: number) {
  await api.delete(`/api_tokens/${id}`)
}

export async function fetchWeatherForecastData(widgetId: number) {
  const { data } = await api.get(`/weather-forecast/widget/${widgetId}/data`)
  return data as {
    hourly: Array<{
      time: string
      temperature: number | null
      felt_temperature: number | null
      humidity: number | null
      pressure: number | null
      wind_speed: number | null
      wind_direction: number | null
      precipitation: number | null
      precipitation_probability: number | null
      cloud_cover: number | null
      uv_index: number | null
      visibility: number | null
      is_daylight: boolean
      pictocode: number | null
    }> | null
    daily: Array<{
      date: string
      temperature_max: number | null
      temperature_min: number | null
      temperature_mean: number | null
      precipitation: number | null
      precipitation_probability: number | null
      precipitation_hours: number | null
      wind_speed_max: number | null
      wind_speed_mean: number | null
      wind_direction: number | null
      humidity_mean: number | null
      pressure_mean: number | null
      uv_index: number | null
      pictocode: number | null
    }> | null
    current: {
      temperature: number | null
      feels_like: number | null
      temperature_min: number | null
      temperature_max: number | null
      pressure: number | null
      humidity: number | null
      wind_speed: number | null
      wind_direction: number | null
      visibility: number | null
      clouds: number | null
      description: string | null
      icon: string | null
      icon_url: string | null
      city: string | null
      country: string | null
      sunrise: string | null
      sunset: string | null
      timestamp: number | null
    } | null
    warnings: Array<{
      headline: string | null
      description: string | null
      event: string | null
      level: number | null
      type: number | null
      start: string | null
      end: string | null
    }>
    dwd_daily: Array<{
      date: string | null
      temperature_min: number | null
      temperature_max: number | null
      icon: string | null
      sunrise: string | null
      sunset: string | null
    }>
  }
}

/* ── Widget Settings ────────────────────────────────────────────────────── */
export async function createWidgetSetting(widgetId: number, payload: { key: string; value: string }) {
  const { data } = await api.post(`/admin/widget/${widgetId}/setting`, payload)
  return data as { success: boolean; id: number }
}

export async function updateWidgetSetting(widgetId: number, settingId: number, payload: { key?: string; value?: string }) {
  const { data } = await api.put(`/admin/widget/${widgetId}/setting/${settingId}`, payload)
  return data
}

export async function deleteWidgetSetting(widgetId: number, settingId: number) {
  await api.delete(`/admin/widget/${widgetId}/setting/${settingId}`)
}

/* ── Widget Weather Forecast ────────────────────────────────────────────── */
export async function setWidgetWeatherForecast(widgetId: number, supplierId: number) {
  const { data } = await api.put(`/admin/widget/${widgetId}/weather-forecast-supplier/${supplierId}`)
  return data
}

export async function removeWidgetWeatherForecast(widgetId: number, supplierId: number) {
  await api.delete(`/admin/widget/${widgetId}/weather-forecast-supplier/${supplierId}`)
}

/* ── Weather Forecast Suppliers ─────────────────────────────────────────── */
export async function fetchWeatherForecastSuppliers() {
  const { data } = await api.get('/weather_forecast_suppliers', { params: { pagination: 'false' } })
  return extractMembers<WeatherForecastSupplierRecord>(data)
}

export async function updateWeatherForecastSupplier(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/weather_forecast_suppliers/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function updateWeatherForecast(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/weather_forecasts/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function syncWeatherForecasts() {
  const { data } = await api.post('/admin/widget/weather-forecast/sync')
  return data as { success: boolean; count: number }
}

/* ── Global Settings ────────────────────────────────────────────────────── */
export async function fetchGlobalSettings() {
  const { data } = await api.get('/admin/settings')
  return data as Record<string, string>
}

export async function saveGlobalSettings(settings: Record<string, string>) {
  const { data } = await api.put('/admin/settings', settings)
  return data
}

export async function sendTestMail() {
  const { data } = await api.post('/admin/settings/mail-test')
  return data as { sentTo: string }
}

export async function searchTrainStations(query: string) {
  const { data } = await api.get('/admin/settings/train-stations/search', {
    params: { q: query },
  })
  return (data.items ?? []) as Array<{ eva: string; name: string; type: string }>
}

export async function fetchPublicSettings() {
  const { data } = await api.get('/settings/public')
  return data as Record<string, string>
}

/* ── Documents ─────────────────────────────────────────────────────────── */
export async function fetchDocuments(params?: Record<string, string>) {
  const { data } = await api.get('/documents', { params: { ...params, pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchDocument(id: number) {
  const { data } = await api.get(`/documents/${id}`)
  return data
}

export async function uploadDocument(formData: FormData) {
  const { data } = await api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function updateDocument(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/documents/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteDocument(id: number) {
  await api.delete(`/documents/${id}`)
}

export async function bulkDeleteDocuments(ids: number[]) {
  const { data } = await api.post('/documents/bulk-delete', { ids })
  return data
}

export async function toggleDocumentCategory(docId: number, categoryId: number) {
  const { data } = await api.post(`/documents/${docId}/toggle-category/${categoryId}`)
  return data
}

export async function classifyDocument(id: number, options?: { forceOcr?: boolean }) {
  const { data } = await api.post(`/documents/${id}/classify`, undefined, {
    params: options?.forceOcr ? { forceOcr: 'true' } : undefined,
  })
  return data
}

/* ── Documents: Admin-Massenverarbeitung & Queue-Status ──────────────────── */
export interface DocumentQueueStatusEntry {
  id: number
  title: string
  status: string
  updatedAt: string
}

export interface DocumentQueueStatusSection {
  counts: Record<string, number>
  pending: DocumentQueueStatusEntry[]
  processing: DocumentQueueStatusEntry[]
  failed: DocumentQueueStatusEntry[]
}

export interface DocumentQueueStatus {
  classification: DocumentQueueStatusSection
  embedding: DocumentQueueStatusSection
}

export async function fetchDocumentQueueStatus() {
  const { data } = await api.get('/admin/documents/queue-status')
  return data as DocumentQueueStatus
}

export interface RequeueDocumentClassificationOptions {
  forceOcr?: boolean
  autoAccept?: boolean
  overwriteProtectedFields?: boolean
  includeFinished?: boolean
}

export async function requeueDocumentClassification(options?: RequeueDocumentClassificationOptions) {
  const { data } = await api.post('/admin/documents/classification/requeue', options ?? {})
  return data as { queued: number; message: string }
}

export async function requeueDocumentEmbeddings(options?: { includeFinished?: boolean }) {
  const { data } = await api.post('/admin/documents/embeddings/requeue', options ?? {})
  return data as { queued: number; message: string }
}

export interface EnhanceScanPage {
  corners: [number, number][]
  colorMode: 'color' | 'grayscale' | 'bw'
  brightness?: number
  contrast?: number
  illumination?: number
  preserveColors?: boolean
}

export async function enhanceDocumentScan(id: number, pages: EnhanceScanPage[]) {
  const { data } = await api.post(`/documents/${id}/enhance-scan`, { pages })
  return data
}

export async function previewDocumentScan(id: number, pageNumber: number, page: EnhanceScanPage): Promise<Blob> {
  const response = await api.post(`/documents/${id}/enhance-scan/preview`, { pageNumber, ...page }, {
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function editDocumentPages(id: number, pages: number[]) {
  const { data } = await api.post(`/documents/${id}/pages`, { pages })
  return data
}

export interface DocumentSplitRange {
  title: string
  pages: number[]
}

export async function mergeDocuments(documentIds: number[], title: string) {
  const { data } = await api.post('/documents/merge', { documentIds, title })
  return data
}

export async function splitDocument(id: number, ranges: DocumentSplitRange[]) {
  const { data } = await api.post(`/documents/${id}/split`, { ranges })
  return data
}

function buildDocumentVersionParams(versionToken?: string | null): URLSearchParams {
  const params = new URLSearchParams()

  if (versionToken) {
    params.set('v', versionToken)
  }

  return params
}

export function getDocumentDownloadUrl(id: number, versionToken?: string | null): string {
  const params = buildDocumentVersionParams(versionToken)
  const query = params.toString()

  return query ? `/api/documents/${id}/download?${query}` : `/api/documents/${id}/download`
}

export async function fetchDocumentPreviewUrl(id: number, versionToken?: string | null): Promise<string> {
  const { data } = await api.get(`/documents/${id}/preview-url`, {
    params: Object.fromEntries(buildDocumentVersionParams(versionToken).entries()),
  })
  return resolveApiDocumentUrl(data.url as string)
}

export async function downloadDocumentBlob(id: number, versionToken?: string | null): Promise<Blob> {
  const response = await api.get(`/documents/${id}/download`, {
    params: Object.fromEntries(buildDocumentVersionParams(versionToken).entries()),
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function fetchDocumentThumbnailBlob(id: number, versionToken?: string | null): Promise<Blob> {
  const response = await api.get(`/documents/${id}/thumbnail`, {
    params: Object.fromEntries(buildDocumentVersionParams(versionToken).entries()),
    responseType: 'blob',
  })
  return response.data as Blob
}

/* ── Document Public Links ───────────────────────────────────────────── */
export interface DocumentPublicLink {
  id: number
  token: string
  createdAt: string
  expiresAt: string | null
  accessCount: number
  lastAccessedAt: string | null
  createdBy: string
}

export interface PublicDocumentInfo {
  title: string
  originalFilename: string
  mimeType: string
  fileSize: number
  detectedDate: string | null
  expiresAt: string | null
  hasThumbnail: boolean
}

export async function fetchDocumentPublicLinks(documentId: number) {
  const { data } = await api.get(`/documents/${documentId}/public-links`, { headers: { Accept: 'application/json' } })
  return data as DocumentPublicLink[]
}

export async function createDocumentPublicLink(documentId: number, expiresInDays: number | null) {
  const { data } = await api.post(
    `/documents/${documentId}/public-links`,
    { expiresInDays },
    { headers: { Accept: 'application/json' } },
  )
  return data as DocumentPublicLink
}

export async function revokeDocumentPublicLink(documentId: number, linkId: number) {
  await api.delete(`/documents/${documentId}/public-links/${linkId}`)
}

export async function fetchPublicDocument(token: string) {
  // Plain axios on purpose: the shared client attaches the stored JWT, and an expired one makes the
  // firewall answer 401 (-> redirect to login) even on these PUBLIC_ACCESS routes.
  const { data } = await axios.get(resolveApiDocumentUrl(`/api/public/documents/${encodeURIComponent(token)}`), {
    headers: { Accept: 'application/json' },
  })
  return data as PublicDocumentInfo
}

export function getPublicDocumentFileUrl(token: string, download = false): string {
  const path = `/api/public/documents/${encodeURIComponent(token)}/file${download ? '?download=1' : ''}`
  return resolveApiDocumentUrl(path)
}

/**
 * Builds the shareable web URL for a public link. Inside the embedded app the
 * page origin is a local asset host, so the configured API origin is used.
 */
export function buildPublicShareUrl(token: string): string {
  const configuredBaseUrl = typeof api.defaults.baseURL === 'string' ? api.defaults.baseURL : ''
  const origin = /^https?:\/\//i.test(configuredBaseUrl)
    ? new URL(configuredBaseUrl).origin
    : window.location.origin
  const basePath = /^https?:\/\//i.test(configuredBaseUrl) ? '/' : import.meta.env.BASE_URL

  return new URL(`${basePath.replace(/\/?$/, '/')}share/${encodeURIComponent(token)}`, origin).toString()
}

export function getPublicDocumentThumbnailUrl(token: string): string {
  return resolveApiDocumentUrl(`/api/public/documents/${encodeURIComponent(token)}/thumbnail`)
}

/* ── Document Types ──────────────────────────────────────────────────── */
export async function fetchDocumentTypes() {
  const { data } = await api.get('/document_types', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createDocumentType(payload: { name: string }) {
  const { data } = await api.post('/document_types', payload)
  return data
}

export async function updateDocumentType(id: number, payload: { name?: string }) {
  const { data } = await api.patch(`/document_types/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteDocumentType(id: number) {
  await api.delete(`/document_types/${id}`)
}

/* ── Document Categories ─────────────────────────────────────────────── */
export async function fetchDocumentCategories() {
  const { data } = await api.get('/document_categories', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createDocumentCategory(payload: { name: string; slug?: string; color?: string; icon?: string | null; hidden?: boolean }) {
  const { data } = await api.post('/document_categories', payload)
  return data
}

export async function updateDocumentCategory(id: number, payload: { name?: string; slug?: string; color?: string; icon?: string | null; hidden?: boolean }) {
  const { data } = await api.patch(`/document_categories/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteDocumentCategory(id: number) {
  await api.delete(`/document_categories/${id}`)
}

export async function suggestDocumentCategoryIcons(payload: { name: string }) {
  const { data } = await api.post('/document_categories/suggest-icons', payload)
  return data as {
    suggestions: Array<{ label: string; icon: string }>
  }
}

export async function fetchDocumentSenders() {
  const { data } = await api.get('/documents/senders')
  return data as string[]
}

export async function fetchSimilarDocuments(id: number) {
  const { data } = await api.get(`/documents/${id}/similar`)
  return data as Array<{
    id: number
    title: string
    documentType: string
    detectedSender: string | null
    detectedAmount: number | null
    detectedDate: string | null
    uploadedAt: string
    score: number
    reasons: string[]
  }>
}

export interface DocumentSearchInterpretation {
  dateFrom: string | null
  dateTo: string | null
  semanticQuery: string
  documentType: string | null
  sender: string | null
  amountMin: number | null
  amountMax: number | null
}

export interface DocumentSearchDiagnostics {
  score: number
  threshold: number
  chunkCount: number
  embeddingModel: string
}

export interface DocumentSearchResult {
  id: number
  title: string
  score: number
  snippet: string
  documentType: string | null
  detectedSender: string | null
  detectedAmount: number | null
  detectedDate: string | null
  uploadedAt: string
  diagnostics?: DocumentSearchDiagnostics | null
}

export interface DocumentSearchNearMiss {
  id: number
  title: string
  score: number
  documentType: string | null
}

export interface DocumentSearchOverallDiagnostics {
  threshold: number
  candidateDocumentCount: number
  embeddingModel: string
}

export interface DocumentSearchResponse {
  query: string
  interpretation: DocumentSearchInterpretation
  relaxedFilters: boolean
  results: DocumentSearchResult[]
  nearMisses?: DocumentSearchNearMiss[]
  searchDiagnostics?: DocumentSearchOverallDiagnostics | null
}

export async function searchDocuments(query: string) {
  const { data } = await api.post('/documents/search', { query })
  return data as DocumentSearchResponse
}

export interface ShareableUser {
  id: number
  displayName: string
  email: string
}

export interface DocumentShare {
  id: number
  sharedWith: ShareableUser
  permission: 'view' | 'edit'
  permissionLabel: string
  createdAt: string
}

export async function fetchShareableUsers() {
  const { data } = await api.get('/documents/shareable-users')
  return data as ShareableUser[]
}

export async function fetchDocumentShares(documentId: number) {
  const { data } = await api.get(`/documents/${documentId}/shares`)
  return data as DocumentShare[]
}

export async function createDocumentShare(documentId: number, sharedWith: number, permission: 'view' | 'edit') {
  const { data } = await api.post(`/documents/${documentId}/shares`, { sharedWith, permission })
  return data as DocumentShare
}

export async function deleteDocumentShare(documentId: number, shareId: number) {
  await api.delete(`/documents/${documentId}/shares/${shareId}`)
}

export async function fetchCategoryShares(categoryId: number) {
  const { data } = await api.get(`/document_categories/${categoryId}/shares`)
  return data as DocumentShare[]
}

export async function createCategoryShare(categoryId: number, sharedWith: number, permission: 'view' | 'edit') {
  const { data } = await api.post(`/document_categories/${categoryId}/shares`, { sharedWith, permission })
  return data as DocumentShare
}

export async function deleteCategoryShare(categoryId: number, shareId: number) {
  await api.delete(`/document_categories/${categoryId}/shares/${shareId}`)
}

/* ── Inventory ─────────────────────────────────────────────────────────── */
export async function fetchInventoryItems() {
  const { data } = await api.get('/inventory_items', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchInventoryItem(id: number) {
  const { data } = await api.get(`/inventory_items/${id}`)
  return data
}

export async function createInventoryItem(payload: Record<string, unknown>) {
  const { data } = await api.post('/inventory_items', payload)
  return data
}

export async function updateInventoryItem(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/inventory_items/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteInventoryItem(id: number) {
  await api.delete(`/inventory_items/${id}`)
}

export async function fetchInventoryLocations() {
  const { data } = await api.get('/inventory_locations', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createInventoryLocation(payload: Record<string, unknown>) {
  const { data } = await api.post('/inventory_locations', payload)
  return data
}

export async function updateInventoryLocation(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/inventory_locations/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteInventoryLocation(id: number) {
  await api.delete(`/inventory_locations/${id}`)
}

export async function fetchInventoryCategories() {
  const { data } = await api.get('/inventory_categories', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createInventoryCategory(payload: Record<string, unknown>) {
  const { data } = await api.post('/inventory_categories', payload)
  return data
}

export async function updateInventoryCategory(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/inventory_categories/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteInventoryCategory(id: number) {
  await api.delete(`/inventory_categories/${id}`)
}

export async function createInventoryCategoryAttribute(payload: Record<string, unknown>) {
  const { data } = await api.post('/inventory_category_attributes', payload)
  return data
}

export async function updateInventoryCategoryAttribute(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/inventory_category_attributes/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteInventoryCategoryAttribute(id: number) {
  await api.delete(`/inventory_category_attributes/${id}`)
}

export async function lookupInventoryItemByQrCode(qrCode: string) {
  const { data } = await api.get(`/inventory_items/lookup/${encodeURIComponent(qrCode)}`)
  return data
}

export async function bookInventoryMovement(itemId: number, payload: unknown) {
  const { data } = await api.post(`/inventory_items/${itemId}/book-movement`, payload)
  return data as {
    movement: Record<string, unknown>
    item: Record<string, unknown>
    message: string
  }
}

export async function fetchInventoryLabelDefinitions() {
  const { data } = await api.get('/inventory_label_definitions', { params: { pagination: 'false' } })
  return extractMembers<InventoryLabelDefinitionRecord>(data)
}

export async function createInventoryLabelDefinition(payload: Record<string, unknown>) {
  const { data } = await api.post('/inventory_label_definitions', payload)
  return data as InventoryLabelDefinitionRecord
}

export async function updateInventoryLabelDefinition(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/inventory_label_definitions/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data as InventoryLabelDefinitionRecord
}

export async function deleteInventoryLabelDefinition(id: number) {
  await api.delete(`/inventory_label_definitions/${id}`)
}

export async function fetchInventoryLabelSheets() {
  const { data } = await api.get('/inventory_label_sheets', { params: { pagination: 'false' } })
  return extractMembers<InventoryLabelSheetRecord>(data)
}

export async function createInventoryLabelSheet(definitionId: number) {
  const { data } = await api.post('/inventory_label_sheets/create-custom', { definitionId })
  return data as InventoryLabelSheetCreateResult
}

export async function assignInventoryItemsToLabelSheet(sheetId: number, itemIds: number[]) {
  const { data } = await api.post(`/inventory_label_sheets/${sheetId}/assign-items`, { itemIds })
  return data as InventoryLabelSheetAssignResult
}

export async function downloadInventoryLabelSheetPdf(sheetId: number): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get(`/inventory_label_sheets/${sheetId}/export/pdf`, {
    responseType: 'blob',
  })

  const disposition = String(response.headers['content-disposition'] ?? '')
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  const encodedName = utf8Match?.[1] ?? plainMatch?.[1] ?? `etikettenbogen-${sheetId}.pdf`

  return {
    blob: response.data as Blob,
    filename: decodeURIComponent(encodedName),
  }
}

/* ── IONOS Admin ──────────────────────────────────────────────────────── */
export async function fetchIonosModels() {
  const { data } = await api.get('/admin/ionos/models')
  return data as {
    models: Array<{ id: string; name: string; type: string; input_price_per_1m: number | null; output_price_per_1m: number | null; pricing_info: string }>
    currentModel: string
  }
}

export async function syncIonosModelPricing(deleteMissing = false) {
  const { data } = await api.post('/admin/ionos/models/sync-pricing', { deleteMissing })
  return data as {
    addedModels: Array<{ modelId: string; displayName: string }>
    obsoleteModels: Array<{ id: number; modelId: string; displayName: string }>
    deletedModels: Array<{ id: number; modelId: string; displayName: string }>
    remoteModelCount: number
  }
}

export async function fetchDocumentStats() {
  const { data } = await api.get('/admin/documents/stats')
  return data as {
    totalDocuments: number
    classifiedDocuments: number
    totalPromptTokens: number
    totalCompletionTokens: number
    totalTokens: number
    totalOcrPromptTokens: number
    totalOcrCompletionTokens: number
    totalOcrTokens: number
    estimatedCostEur: number
    costPerModel: Array<{ model: string; source: string; promptTokens: number; completionTokens: number; costEur: number }>
  }
}

export interface DocumentCostPerModel {
  model: string
  source: string
  promptTokens: number
  completionTokens: number
  costEur: number
}

export interface DocumentMonthlyStatsItem {
  month: number
  label: string
  totalDocuments: number
  classifiedDocuments: number
  totalPromptTokens: number
  totalCompletionTokens: number
  totalTokens: number
  totalOcrPromptTokens: number
  totalOcrCompletionTokens: number
  totalOcrTokens: number
  estimatedCostEur: number
  costPerModel: DocumentCostPerModel[]
}

export async function fetchDocumentMonthlyStats(year: number) {
  const { data } = await api.get('/admin/documents/stats/monthly', { params: { year } })
  return data as {
    year: number
    months: DocumentMonthlyStatsItem[]
  }
}

export async function fetchAiModelPricing() {
  const { data } = await api.get('/ai_model_pricings', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createAiModelPricing(payload: { modelId: string; displayName: string; inputPricePer1m: string; outputPricePer1m: string; active?: boolean }) {
  const { data } = await api.post('/ai_model_pricings', payload)
  return data
}

export async function updateAiModelPricing(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/ai_model_pricings/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteAiModelPricing(id: number) {
  await api.delete(`/ai_model_pricings/${id}`)
}

export async function fetchIonosBilling() {
  const { data } = await api.get('/admin/ionos/billing')
  return data
}

/* ── Billing: Properties ───────────────────────────────────────────────── */
export async function fetchProperties() {
  const { data } = await api.get('/properties', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchProperty(id: number) {
  const { data } = await api.get(`/properties/${id}`)
  return data
}

export async function createProperty(payload: Record<string, unknown>) {
  const { data } = await api.post('/properties', payload)
  return data
}

export async function updateProperty(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/properties/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteProperty(id: number) {
  await api.delete(`/properties/${id}`)
}

/* ── Billing: Rental Units ────────────────────────────────────────────── */
export async function fetchRentalUnits(propertyId?: number) {
  const params: Record<string, string> = { pagination: 'false' }
  if (propertyId) params.property = String(propertyId)
  const { data } = await api.get('/rental_units', { params })
  return extractMembers(data)
}

export async function fetchRentalUnit(id: number) {
  const { data } = await api.get(`/rental_units/${id}`)
  return data
}

export async function createRentalUnit(payload: Record<string, unknown>) {
  const { data } = await api.post('/rental_units', payload)
  return data
}

export async function updateRentalUnit(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/rental_units/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteRentalUnit(id: number) {
  await api.delete(`/rental_units/${id}`)
}

/* ── Billing: Tenants ──────────────────────────────────────────────────── */
export async function fetchTenants() {
  const { data } = await api.get('/tenants', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createTenant(payload: Record<string, unknown>) {
  const { data } = await api.post('/tenants', payload)
  return data
}

export async function updateTenant(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/tenants/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteTenant(id: number) {
  await api.delete(`/tenants/${id}`)
}

export async function fetchTenant(id: number) {
  const { data } = await api.get(`/tenants/${id}`)
  return data
}

export async function fetchTenantBillingResults(tenantId: number) {
  const { data } = await api.get(`/tenants/${tenantId}/billing-results`)
  return data
}

/* ── Billing: Tenancies ────────────────────────────────────────────────── */
export async function fetchTenancies(rentalUnitId?: number) {
  const params: Record<string, string> = { pagination: 'false' }
  if (rentalUnitId) params.rentalUnits = String(rentalUnitId)
  const { data } = await api.get('/tenancies', { params })
  return extractMembers(data)
}

export async function fetchTenancy(id: number) {
  const { data } = await api.get(`/tenancies/${id}`)
  return data
}

export async function createTenancy(payload: Record<string, unknown>) {
  const { data } = await api.post('/tenancies', payload)
  return data
}

export async function updateTenancy(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/tenancies/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteTenancy(id: number) {
  await api.delete(`/tenancies/${id}`)
}

/* ── Hausverwaltung: Rooms ─────────────────────────────────────────────── */
export interface RoomImageRecord {
  id: number
  originalFilename: string
  mimeType: string
  fileSize: number
  fileHash: string | null
  takenAt: string | null
  note: string | null
  uploadedAt: string
}

export interface RoomRecord {
  id: number
  name: string
  description: string | null
  icon: string | null
  property: string | null
  rentalUnit: string | null
  imageCount: number
  images?: RoomImageRecord[]
}

export async function fetchRooms(filter?: { property?: number; rentalUnit?: number }) {
  const params: Record<string, string> = { pagination: 'false' }
  if (filter?.property) params.property = String(filter.property)
  if (filter?.rentalUnit) params.rentalUnit = String(filter.rentalUnit)
  const { data } = await api.get('/rooms', { params })
  return extractMembers(data)
}

export async function fetchRoom(id: number) {
  const { data } = await api.get(`/rooms/${id}`)
  return data
}

export async function createRoom(payload: Record<string, unknown>) {
  const { data } = await api.post('/rooms', payload)
  return data
}

export async function updateRoom(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/rooms/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteRoom(id: number) {
  await api.delete(`/rooms/${id}`)
}

export async function uploadRoomImage(roomId: number, formData: FormData) {
  const { data } = await api.post(`/rooms/${roomId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function updateRoomImage(id: number, payload: { takenAt?: string | null; note?: string | null }) {
  const { data } = await api.patch(`/room_images/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteRoomImage(id: number) {
  await api.delete(`/room_images/${id}`)
}

export async function fetchRoomImagePreviewUrl(id: number, versionToken?: string | null): Promise<string> {
  const { data } = await api.get(`/room_images/${id}/preview-url`, {
    params: versionToken ? { v: versionToken } : undefined,
  })
  return resolveApiDocumentUrl(data.url as string)
}

/* ── Billing: Floor Plans ──────────────────────────────────────────────── */
export interface FloorPlanRecord {
  id: number
  rentalUnit: string
  data: FloorPlanDocument
  createdAt: string
  updatedAt: string
}

/** Returns the (single) floor plan of a rental unit, or null if none exists. */
export async function fetchFloorPlan(rentalUnitId: number): Promise<FloorPlanRecord | null> {
  const { data } = await api.get('/floor_plans', {
    params: { rentalUnit: String(rentalUnitId), pagination: 'false' },
  })
  const members = extractMembers<FloorPlanRecord>(data)
  return members[0] ?? null
}

/** Returns all floor plans of a property's rental units in a single request. */
export async function fetchFloorPlansByProperty(propertyId: number): Promise<FloorPlanRecord[]> {
  const { data } = await api.get('/floor_plans', {
    params: { 'rentalUnit.property': String(propertyId), pagination: 'false' },
  })
  return extractMembers<FloorPlanRecord>(data)
}

export async function createFloorPlan(payload: {
  rentalUnit: string
  data: FloorPlanDocument
}): Promise<FloorPlanRecord> {
  const { data } = await api.post('/floor_plans', payload)
  return data
}

export async function updateFloorPlan(
  id: number,
  payload: { data: FloorPlanDocument },
): Promise<FloorPlanRecord> {
  const { data } = await api.patch(`/floor_plans/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteFloorPlan(id: number) {
  await api.delete(`/floor_plans/${id}`)
}

/* ── Billing: Billing Periods ──────────────────────────────────────────── */
export async function fetchBillingPeriods(filter?: { property?: number; tenancy?: number }) {
  const params: Record<string, string> = { pagination: 'false' }
  if (filter?.property) params.property = String(filter.property)
  if (filter?.tenancy) params.tenancy = String(filter.tenancy)
  const { data } = await api.get('/billing_periods', { params })
  return extractMembers(data)
}

export async function fetchBillingPeriod(id: number) {
  const { data } = await api.get(`/billing_periods/${id}`)
  return data
}

export async function createBillingPeriod(payload: Record<string, unknown>) {
  const { data } = await api.post('/billing_periods', payload)
  return data
}

export async function updateBillingPeriod(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/billing_periods/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteBillingPeriod(id: number) {
  await api.delete(`/billing_periods/${id}`)
}

/* ── Billing: Cost Types ───────────────────────────────────────────────── */
export async function fetchCostTypes() {
  const { data } = await api.get('/cost_types', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createCostType(payload: Record<string, unknown>) {
  const { data } = await api.post('/cost_types', payload)
  return data
}

export async function updateCostType(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/cost_types/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteCostType(id: number) {
  await api.delete(`/cost_types/${id}`)
}

/* ── Billing: Cost Entries ─────────────────────────────────────────────── */
export async function fetchCostEntries(billingPeriodId?: number) {
  const params: Record<string, string> = { pagination: 'false' }
  if (billingPeriodId) params.billingPeriod = String(billingPeriodId)
  const { data } = await api.get('/cost_entries', { params })
  return extractMembers(data)
}

export async function fetchCostEntry(id: number) {
  const { data } = await api.get(`/cost_entries/${id}`)
  return data
}

export async function createCostEntry(payload: Record<string, unknown>) {
  const { data } = await api.post('/cost_entries', payload)
  return data
}

export async function updateCostEntry(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/cost_entries/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteCostEntry(id: number) {
  await api.delete(`/cost_entries/${id}`)
}

/* ── Billing: Meter Readings ───────────────────────────────────────────── */
export async function fetchMeterReadings(billingPeriodId?: number, rentalUnitId?: number) {
  const params: Record<string, string> = { pagination: 'false' }
  if (billingPeriodId) params.billingPeriod = String(billingPeriodId)
  if (rentalUnitId) params.rentalUnit = String(rentalUnitId)
  const { data } = await api.get('/meter_readings', { params })
  return extractMembers(data)
}

export async function createMeterReading(payload: Record<string, unknown>) {
  const { data } = await api.post('/meter_readings', payload)
  return data
}

export async function deleteMeterReading(id: number) {
  await api.delete(`/meter_readings/${id}`)
}

export async function importMeterReadings(rentalUnitId: number, billingPeriodId: number, sensorId: number) {
  const { data } = await api.post(`/rental-units/${rentalUnitId}/import-meter-readings`, {
    billingPeriodId,
    sensorId,
  })
  return data
}

/** Die drei scanbaren Medien. Entspricht dem `{kind}`-Segment der Scan-Route. */
export type MeterKind = 'water' | 'gas' | 'electricity'

export const METER_SENSOR_TYPES: Record<MeterKind, string> = {
  water: 'water_consumption',
  gas: 'gas_consumption',
  electricity: 'electricity_consumption',
}

export interface MeterScanSensor {
  id: number
  name: string
  meter_number: string | null
  unit: string
  current: number
}

/* ── Billing: Zähler-Scan-Queue ─────────────────────────────────────────── */

export type MeterScanStatus = 'pending' | 'processing' | 'review' | 'failed' | 'applied' | 'discarded'

/**
 * Ein Eintrag der Prüf-Queue, wie ihn `GET /api/meter_scans` liefert
 * (JSON-LD, deshalb camelCase).
 */
export interface MeterScanEntry {
  '@id'?: string
  id: number
  sensorType: string
  status: MeterScanStatus
  capturedAt: string
  uploadedAt: string
  processedAt?: string | null
  recognizedMeterNumber?: string | null
  recognizedValue?: number | null
  appliedMeterNumber?: string | null
  appliedValue?: number | null
  confidence: number
  attempts: number
  errorMessage?: string | null
  /** Das Foto wird nach Übernahme, Verwerfen und Ablauf der Aufbewahrung gelöscht. */
  hasImage: boolean
}

/**
 * Der Datensatz, wie ihn die Aktions-Endpunkte zurückgeben (snake_case, mit
 * bereits aufgelöstem Medium und Status-Label).
 */
export interface MeterScanSummary {
  id: number
  kind: MeterKind | null
  sensor_type: string
  status: MeterScanStatus
  status_label: string
  client_uuid: string | null
  captured_at: string
  uploaded_at: string
  has_image: boolean
  meter_number: string | null
  value: number | null
  confidence: number
}

export interface MeterScanQueued {
  error: false
  message: string
  scan: MeterScanSummary
}

export interface MeterScanApplied extends MeterScanQueued {
  sensor: MeterScanSensor | null
}

/**
 * Legt ein Foto in der serverseitigen Prüf-Queue ab, ohne es auszuwerten.
 *
 * Das Medium gehört zur Zuordnung, nicht nur zur Bilderkennung: eine Nummer
 * darf an einem Gas- und einem Wasserzähler existieren, und der Stand muss
 * beim richtigen landen.
 *
 * `capturedAt` stammt aus dem Moment der Aufnahme — der Upload passiert oft
 * Stunden später, wenn wieder Netz da ist. `clientUuid` macht den Upload
 * idempotent: kommt die Antwort auf wackliger Leitung nicht zurück, erzeugt
 * der zweite Versuch keinen zweiten Eintrag.
 */
export async function queueMeterScan(
  kind: MeterKind,
  file: Blob,
  options: { capturedAt?: string; clientUuid?: string; filename?: string } = {},
): Promise<MeterScanQueued> {
  const formData = new FormData()
  formData.append(
    'file',
    file,
    options.filename ?? (file instanceof File ? file.name : 'zaehler.jpg'),
  )
  if (options.capturedAt) formData.append('capturedAt', options.capturedAt)
  if (options.clientUuid) formData.append('clientUuid', options.clientUuid)

  const { data } = await api.post(`/meter-scan/${kind}/queue`, formData, {
    headers: { 'Content-Type': 'multipart/form-data', Accept: 'application/json' },
  })
  return data
}

export async function fetchMeterScans(
  options: { status?: MeterScanStatus | MeterScanStatus[]; itemsPerPage?: number } = {},
): Promise<{ items: MeterScanEntry[]; totalItems: number }> {
  const params: Record<string, unknown> = {
    'order[capturedAt]': 'desc',
    itemsPerPage: options.itemsPerPage ?? 50,
  }
  // Axios hängt bei Array-Werten selbst `[]` an den Schlüssel — genau das
  // erwartet der SearchFilter für eine Oder-Auswahl mehrerer Status.
  if (options.status) {
    params.status = options.status
  }

  const { data } = await api.get('/meter_scans', { params })

  return { items: extractMembers<MeterScanEntry>(data), totalItems: extractTotalItems(data) }
}

/**
 * Kurzlebige, signierte URL für die Bildvorschau. Das Bild selbst hängt nicht
 * am JWT, damit es direkt in ein `<img>` darf.
 */
export async function fetchMeterScanImageUrl(id: number): Promise<string> {
  const { data } = await api.get(`/meter_scans/${id}/image-url`)
  return data.url
}

/**
 * Ein Zähler, auf den sich ein geprüfter Stand buchen lässt — mit dem Ort, an
 * dem er hängt. `floor` stammt von der Mieteinheit; ein Hauszähler ohne
 * Mieteinheit hat keins.
 */
export interface MeterScanMeterOption {
  id: number
  name: string
  meter_number: string
  sensor_type: string
  floor: number | null
  rental_unit: string | null
  property: string | null
}

/** Alle Zähler mit Nummer, über alle Medien — die Auswahl der Prüfmaske. */
export async function fetchMeterScanMeters(): Promise<MeterScanMeterOption[]> {
  const { data } = await api.get<{ meters: MeterScanMeterOption[] }>('/meter-scan/meters')
  return data.meters
}

/**
 * Schreibt den geprüften Zählerstand dem ausgewählten Zähler gut.
 * Die Werte kommen aus dem Formular, nicht aus der Erkennung — der Nutzer
 * darf sie vorher korrigieren.
 */
export async function applyMeterScan(
  id: number,
  reading: { sensorId: number; value: number; capturedAt?: string },
): Promise<MeterScanApplied> {
  const { data } = await api.post(`/meter-scan/queue/${id}/apply`, {
    sensor_id: reading.sensorId,
    value: reading.value,
    captured_at: reading.capturedAt,
  })
  return data
}

export async function discardMeterScan(id: number): Promise<MeterScanQueued> {
  const { data } = await api.post(`/meter-scan/queue/${id}/discard`)
  return data
}

export async function retryMeterScan(id: number): Promise<MeterScanQueued> {
  const { data } = await api.post(`/meter-scan/queue/${id}/retry`)
  return data
}

/* ── Billing: Zähler anlegen ────────────────────────────────────────────── */

export interface MeterProvisionRequest {
  sensor_type: string
  name: string
  meter_number?: string | null
  rental_unit_id?: number
  property_id?: number
}

/**
 * Was das Anlegen eines Zählers anfassen würde. `action` unterscheidet je
 * Schritt, ob ein vorhandener Datensatz wiederverwendet oder ein neuer
 * angelegt wird — die Grundlage für den Bestätigungsdialog.
 */
export interface MeterProvisionPlan {
  location: { id: number | null; city: string; action: 'create' | 'reuse' }
  device: { id: number | null; name: string; action: 'create' | 'reuse' }
  sensor: { name: string; meter_number: string | null; sensor_type: string; action: 'create' }
  assignment: { type: 'rental_unit' | 'property'; id: number; name: string }
  warnings: string[]
}

export interface MeterProvisionResult {
  error: false
  message: string
  sensor: { id: number; name: string; meter_number: string | null; sensor_type: string; unit: string }
  device: { id: number; name: string }
  plan: MeterProvisionPlan
}

/** Legt nichts an — beschreibt nur, was `createMeterProvision` tun würde. */
export async function previewMeterProvision(payload: MeterProvisionRequest): Promise<MeterProvisionPlan> {
  const { data } = await api.post('/billing/meters/preview', payload)
  return data
}

export async function createMeterProvision(payload: MeterProvisionRequest): Promise<MeterProvisionResult> {
  const { data } = await api.post('/billing/meters', payload)
  return data
}

/* ── Billing: Own Labor Entries ─────────────────────────────────────────── */
export async function fetchOwnLaborEntries(billingPeriodId?: number) {
  const params: Record<string, string> = { pagination: 'false' }
  if (billingPeriodId) params.billingPeriod = String(billingPeriodId)
  const { data } = await api.get('/own_labor_entries', { params })
  return extractMembers(data)
}

export async function createOwnLaborEntry(payload: Record<string, unknown>) {
  const { data } = await api.post('/own_labor_entries', payload)
  return data
}

export async function updateOwnLaborEntry(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/own_labor_entries/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteOwnLaborEntry(id: number) {
  await api.delete(`/own_labor_entries/${id}`)
}

/* ── Billing: Results ──────────────────────────────────────────────────── */
export async function fetchBillingResults(billingPeriodId?: number) {
  const params: Record<string, string> = { pagination: 'false' }
  if (billingPeriodId) params.billingPeriod = String(billingPeriodId)
  const { data } = await api.get('/billing_results', { params })
  return extractMembers(data)
}

export async function fetchBillingResult(id: number) {
  const { data } = await api.get(`/billing_results/${id}`)
  return data
}

/* ── Billing: Calculate & Export ───────────────────────────────────────── */
export async function calculateBillingPeriod(billingPeriodId: number) {
  const { data } = await api.post(`/billing-periods/${billingPeriodId}/calculate`)
  return data
}

export async function downloadBillingPdf(periodId: number): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get(`/billing-periods/${periodId}/export/pdf`, {
    responseType: 'blob',
  })

  const disposition = String(response.headers['content-disposition'] ?? '')
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  const plainMatch = disposition.match(/filename="?([^";]+)"?/i)
  const encodedName = utf8Match?.[1] ?? plainMatch?.[1] ?? `abrechnung-${periodId}.pdf`

  return {
    blob: response.data as Blob,
    filename: decodeURIComponent(encodedName),
  }
}

/* ── Hausverwaltung: Invoice Dashboard ─────────────────────────────────── */
export async function fetchInvoiceSummary(propertyId: number, year: number) {
  const { data } = await api.get(`/properties/${propertyId}/invoice-summary`, { params: { year } })
  return data
}

export async function fetchInvoiceYears(propertyId: number) {
  const { data } = await api.get(`/properties/${propertyId}/invoice-years`)
  return data.years ?? []
}

export async function fetchBuildingInvoices(propertyId: number, year: number) {
  const { data } = await api.get(`/properties/${propertyId}/building-invoices`, { params: { year } })
  return data.entries ?? []
}

export async function fetchUnitInvoices(propertyId: number, year: number) {
  const { data } = await api.get(`/properties/${propertyId}/unit-invoices`, { params: { year } })
  return data.units ?? []
}

export async function fetchYearlyTotals(propertyId: number) {
  const { data } = await api.get(`/properties/${propertyId}/yearly-totals`)
  return data.totals ?? []
}

export async function fetchRentalIncome(propertyId: number, year: number) {
  const { data } = await api.get(`/properties/${propertyId}/rental-income`, { params: { year } })
  return data
}

export interface LedgerCategorizeResult {
  processed: number
  categorized: number
  aiFailed: number
  byCategory: Record<string, number>
  remaining: number
}

/** Ordnet bis zu 20 Belege ohne Buchungskategorie per KI (Fallback Regeln) ein. */
export async function categorizePropertyLedger(propertyId: number): Promise<LedgerCategorizeResult> {
  const { data } = await api.post(`/properties/${propertyId}/ledger-categorize`)
  return data
}

export async function fetchYearlyProfit(propertyId: number) {
  const { data } = await api.get(`/properties/${propertyId}/yearly-profit`)
  return data.years ?? []
}

/* ── Hausverwaltung: Manuelle Posten (PropertyLedgerEntry) ─────────────── */
export async function fetchPropertyLedgerEntries(propertyId: number, type?: 'income' | 'expense') {
  const params: Record<string, string> = { property: String(propertyId), pagination: 'false' }
  if (type) params.type = type
  const { data } = await api.get('/property_ledger_entries', { params })
  return extractMembers(data)
}

export async function fetchPropertyLedgerEntry(id: number) {
  const { data } = await api.get(`/property_ledger_entries/${id}`)
  return data
}

export async function createPropertyLedgerEntry(payload: Record<string, unknown>) {
  const { data } = await api.post('/property_ledger_entries', payload)
  return data
}

export async function updatePropertyLedgerEntry(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/property_ledger_entries/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deletePropertyLedgerEntry(id: number) {
  await api.delete(`/property_ledger_entries/${id}`)
}

/* ── Hausverwaltung: Property/Unit Documents ───────────────────────────── */
export async function fetchDocumentsByProperty(propertyId: number) {
  const { data } = await api.get('/documents', { params: { property: String(propertyId), pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchDocumentsByUnit(rentalUnitId: number) {
  const { data } = await api.get('/documents', { params: { rentalUnit: String(rentalUnitId), pagination: 'false' } })
  return extractMembers(data)
}

/* ── Billing: Batch Meter Import + Water Cost Extraction ───────────────── */
export async function batchImportMeterReadings(billingPeriodId: number) {
  const { data } = await api.post(`/billing-periods/${billingPeriodId}/import-meter-readings`)
  return data as { message: string; created: number; updated: number }
}

export async function fetchCostShares(billingPeriodId: number) {
  const { data } = await api.get(`/billing-periods/${billingPeriodId}/cost-shares`)
  return data as { costEntryId: number; share: number; amount: number }[]
}

export async function fetchWaterConsumption(billingPeriodId: number) {
  const { data } = await api.get(`/billing-periods/${billingPeriodId}/water-consumption`)
  return data as { consumption: number | null; rentalUnit: string }
}

export async function extractWaterCosts(documentId: number) {
  const { data } = await api.post(`/documents/${documentId}/extract-water-costs`)
  return data as {
    fresh_water_amount: number
    waste_water_amount: number
    rain_water_amount: number
    base_fee_amount: number
    total_amount: number
    vat_rate: number
    billing_period_text: string | null
    meter_reading_start: number | null
    meter_reading_end: number | null
    consumption: number | null
  }
}

export interface WaterCostImportPayload {
  documentId: number
  freshWaterAmount: number
  baseFeeAmount: number
  wasteWaterAmount: number
  rainWaterAmount: number
  consumption: number | null
}

export async function importWaterCosts(billingPeriodId: number, payload: WaterCostImportPayload) {
  const { data } = await api.post(`/billing-periods/${billingPeriodId}/import-water-costs`, payload)
  return data as {
    message: string
    created: Array<{ costType: string; amount: number; description: string }>
    total: number
  }
}

/* ── Todo ─────────────────────────────────────────────────────────── */

export async function fetchTodoGroups() {
  const { data } = await api.get('/todo_groups', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function createTodoGroup(payload: Record<string, unknown>) {
  const { data } = await api.post('/todo_groups', payload)
  return data
}

export async function updateTodoGroup(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/todo_groups/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteTodoGroup(id: number) {
  await api.delete(`/todo_groups/${id}`)
}

export async function fetchTodoTasks(params?: Record<string, unknown>) {
  const { data } = await api.get('/todo_tasks', { params: { pagination: 'false', ...params } })
  return extractMembers(data)
}

export async function createTodoTask(payload: Record<string, unknown>) {
  const { data } = await api.post('/todo_tasks', payload)
  return data
}

export async function updateTodoTask(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/todo_tasks/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteTodoTask(id: number) {
  await api.delete(`/todo_tasks/${id}`)
}

// ─── Flights & Airports ─────────────────────────────────────────────────────

export async function fetchAirports() {
  const { data } = await api.get('/airports', { params: { pagination: 'false' } })
  return extractMembers(data)
}

export async function fetchFlights(params?: Record<string, unknown>) {
  const { data } = await api.get('/flights', { params: { pagination: 'false', ...params } })
  return extractMembers(data)
}

export async function createFlight(payload: Record<string, unknown>) {
  const { data } = await api.post('/flights', payload)
  return data
}

export async function updateFlight(id: number, payload: Record<string, unknown>) {
  const { data } = await api.patch(`/flights/${id}`, payload, {
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })
  return data
}

export async function deleteFlight(id: number) {
  await api.delete(`/flights/${id}`)
}

export async function fetchFlightStatistics() {
  const { data } = await api.get('/flights/statistics')
  return data
}
